/* ProductPageClient - Client component for dynamic product page with ContactBar and homepage-style sections
 * Updated: H1 simplified to just the heading text (icon and scientific name removed).
 *           Plant info card now uses laurier-white.webp icon and shows full description without expand/collapse.
 *           Breadcrumb includes subcategory step when the product belongs to one.
 */


import { sanitizeHtml } from '@/lib/sanitize-html';
import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/lib/supabase';
import { calculatePriceInclBTW } from '@/lib/btw-calculator';
import { getProductImageUrl } from '@/lib/image-url';
import { marked } from 'marked';

marked.setOptions({
  breaks: true,
  gfm: true,
  pedantic: false,
});

// Configure marked renderer to add target="_blank" to all links
const renderer = new marked.Renderer();
const originalLinkRenderer = renderer.link.bind(renderer);
renderer.link = (href: string, title: string | null | undefined, text: string) => {
  const html = originalLinkRenderer(href, title, text);
  return html.replace('<a', '<a target="_blank" rel="noopener noreferrer"');
};
marked.use({ renderer });

import ContactBar from '@/components/ContactBar';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageBreadcrumb, { Crumb } from '@/components/PageBreadcrumb';
import ProductVariantCard from '@/components/product/ProductVariantCard';
import ProductFAQ from '@/components/product/ProductFAQ';
import OfferteCTA from '@/components/product/OfferteCTA';
import SimpleProductPageClient from '@/components/product/SimpleProductPageClient';
import ProductVariantPageClient from '@/components/product/ProductVariantPageClient';
import NotFoundPage from '@/pages/NotFoundPage';
import { Loader as Loader2 } from 'lucide-react';
import { trackGoal } from '@/lib/datafast';

interface Product {
  id: string;
  name: string;
  scientific_name: string;
  description: string;
  standplaats: string;
  groeisnelheid: string;
  snoeien: string;
  category_name: string;
  category_slug: string;
  base_slug: string;
  is_simple_product?: boolean;
  subcategory_name: string | null;
  subcategory_slug: string | null;
}

interface ProductVariant {
  id: string;
  size: string;
  type: string;
  price_tier_1: string;
  price_tier_2?: string | null;
  price_tier_3?: string | null;
  price_tier_4?: string | null;
  minimum_order_quantity: number;
  stock_quantity: number;
  sku: string;
  slug: string;
  images: string[];
  variantImages: string[];
  genericImages: string[];
  price?: string;
  supplier?: string;
}

interface ProductFAQItem {
  id: string;
  question: string;
  answer: string;
  display_order: number;
}

export default function ProductPageClient() {
  const { category, product: productSlug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [faqs, setFaqs] = useState<ProductFAQItem[]>([]);
  const [isSimpleProduct, setIsSimpleProduct] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [isVariantPage, setIsVariantPage] = useState(false);

  const descriptionHtml = useMemo(() => {
    if (!product?.description) return '';
    return sanitizeHtml(marked.parse(product.description) as string);
  }, [product?.description]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (shouldRedirect && redirectUrl) {
      navigate(redirectUrl);
    }
  }, [shouldRedirect, redirectUrl, navigate]);

  useEffect(() => {
    const fetchProductData = async () => {
      if (!productSlug || !category) return;

      try {
        setLoading(true);
        setIsVariantPage(false);
        setIsSimpleProduct(false);
        setShouldRedirect(false);
        setProduct(null);
        setVariants([]);
        setFaqs([]);

        // Check if this is a variant-specific URL (contains size and type at the end)
        const slugParts = productSlug.split('-');
        const lastPart = slugParts[slugParts.length - 1];
        const secondLastPart = slugParts[slugParts.length - 2];
        const thirdLastPart = slugParts[slugParts.length - 3];
        const fourthLastPart = slugParts[slugParts.length - 4];

        // Check if the last parts match variant pattern
        // Handle 'kale-wortel' case where it's split into 'kale' and 'wortel'
        const isKaleWortelType = lastPart === 'wortel' && secondLastPart === 'kale' && !isNaN(parseInt(thirdLastPart)) && !isNaN(parseInt(fourthLastPart));
        const isOtherType = (lastPart === 'kluit' || lastPart === 'pot' || lastPart === 'container') && !isNaN(parseInt(secondLastPart)) && !isNaN(parseInt(thirdLastPart));

        const isVariantUrl = slugParts.length >= 3 && (isKaleWortelType || isOtherType);

        if (isVariantUrl) {
          setIsVariantPage(true);
          setLoading(false);
          return;
        }

        const fullSlug = `${category}/${productSlug}`;

        const { data: productData, error: productError } = await supabase
          .from('products')
          .select(`
            id,
            name,
            scientific_name,
            description,
            standplaats,
            groeisnelheid,
            snoeien,
            base_slug,
            btw_percentage,
            is_simple_product,
            subcategory_id,
            product_categories(name, slug)
          `)
          .eq('base_slug', fullSlug)
          .eq('active', true)
          .maybeSingle();

        if (productError) throw productError;

        if (!productData) {
          // Fallback: the slug may be a variant slug that didn't match the
          // numeric pattern check above (e.g. non-numeric sizes like "Per m2").
          // Try looking it up directly in product_variants.
          const { data: variantCheck } = await supabase
            .from('product_variants')
            .select('id')
            .eq('slug', fullSlug)
            .eq('active', true)
            .maybeSingle();

          if (variantCheck) {
            setIsVariantPage(true);
            setLoading(false);
            return;
          }
        }

        if (productData) {
          // Check if this is a simple product
          if (productData.is_simple_product) {
            setIsSimpleProduct(true);
            setLoading(false);
            return;
          }

          let subcategoryName: string | null = null;
          let subcategorySlug: string | null = null;
          if (productData.subcategory_id) {
            const { data: subData } = await supabase
              .from('product_subcategories')
              .select('name, slug')
              .eq('id', productData.subcategory_id)
              .maybeSingle();
            if (subData) {
              subcategoryName = subData.name;
              subcategorySlug = subData.slug;
            }
          }

          setProduct({
            id: productData.id,
            name: productData.name,
            scientific_name: productData.scientific_name,
            description: productData.description,
            standplaats: productData.standplaats,
            groeisnelheid: productData.groeisnelheid,
            snoeien: productData.snoeien,
            category_name: (productData.product_categories as any)?.name || '',
            category_slug: (productData.product_categories as any)?.slug || '',
            base_slug: productData.base_slug,
            is_simple_product: productData.is_simple_product,
            subcategory_name: subcategoryName,
            subcategory_slug: subcategorySlug
          });
          trackGoal('product_viewed', { name: productData.name });

          const { data: variantsData, error: variantsError } = await supabase
            .from('product_variants')
            .select('id, size, type, price_tier_1, price_tier_2, price_tier_3, price_tier_4, minimum_order_quantity, stock_quantity, sku, slug, active, supplier, available_from')
            .eq('product_id', productData.id)
            .eq('active', true);

          if (variantsError) throw variantsError;

          if (variantsData) {
            const variantsWithImages = await Promise.all(
              variantsData.map(async (variant: any) => {
                // Fetch variant-specific images
                const { data: variantImages } = await supabase
                  .from('product_images')
                  .select('storage_path, uses_new_bucket')
                  .eq('product_id', productData.id)
                  .eq('variant_id', variant.id)
                  .order('display_order');

                // Fetch generic product images
                const { data: genericImages } = await supabase
                  .from('product_images')
                  .select('storage_path, uses_new_bucket')
                  .eq('product_id', productData.id)
                  .is('variant_id', null)
                  .order('display_order');

                const variantImageUrls = variantImages?.map((img: { storage_path: string; uses_new_bucket: boolean }) =>
                  getProductImageUrl(img.storage_path, img.uses_new_bucket, 'large')
                ) || [];

                const genericImageUrls = genericImages?.map((img: { storage_path: string; uses_new_bucket: boolean }) =>
                  getProductImageUrl(img.storage_path, img.uses_new_bucket, 'large')
                ) || [];

                // Combine all images for backward compatibility
                const allImageUrls = [...variantImageUrls, ...genericImageUrls];

                const btwPercentage = productData.btw_percentage || 9;
                const priceTier1ExclBTW = parseFloat(variant.price_tier_1) || parseFloat(variant.price) || 0;
                const priceTier2ExclBTW = variant.price_tier_2 ? parseFloat(variant.price_tier_2) : null;
                const priceTier3ExclBTW = variant.price_tier_3 ? parseFloat(variant.price_tier_3) : null;
                const priceTier4ExclBTW = variant.price_tier_4 ? parseFloat(variant.price_tier_4) : null;

                return {
                  id: variant.id,
                  size: variant.size,
                  type: variant.type,
                  price_tier_1: calculatePriceInclBTW(priceTier1ExclBTW, btwPercentage).toString(),
                  price_tier_2: priceTier2ExclBTW ? calculatePriceInclBTW(priceTier2ExclBTW, btwPercentage).toString() : null,
                  price_tier_3: priceTier3ExclBTW ? calculatePriceInclBTW(priceTier3ExclBTW, btwPercentage).toString() : null,
                  price_tier_4: priceTier4ExclBTW ? calculatePriceInclBTW(priceTier4ExclBTW, btwPercentage).toString() : null,
                  minimum_order_quantity: variant.minimum_order_quantity || 25,
                  stock_quantity: variant.stock_quantity,
                  sku: variant.sku,
                  slug: variant.slug,
                  images: allImageUrls,
                  variantImages: variantImageUrls,
                  genericImages: genericImageUrls,
                  price: calculatePriceInclBTW(priceTier1ExclBTW, btwPercentage).toString(),
                  supplier: variant.supplier || 'Kwekerij',
                  available_from: variant.available_from || null
                };
              })
            );

            // Sort variants by size (lowest to highest)
            const sortedVariants = variantsWithImages.sort((a, b) => {
              const aNum = parseInt(a.size.split('-')[0]);
              const bNum = parseInt(b.size.split('-')[0]);
              return aNum - bNum;
            });

            // If there's only one variant, redirect to the variant page
            if (sortedVariants.length === 1) {
              const variant = sortedVariants[0];
              const variantUrl = `/${variant.slug}`;
              setRedirectUrl(variantUrl);
              setShouldRedirect(true);
              setLoading(false);
              return;
            }

            setVariants(sortedVariants);
          }

          const { data: faqsData, error: faqsError } = await supabase
            .from('product_faqs')
            .select('*')
            .eq('product_id', productData.id)
            .eq('active', true)
            .order('display_order');

          if (faqsError) throw faqsError;

          if (faqsData) {
            setFaqs(faqsData);
          }
        }
      } catch (error) {
        console.error('Error fetching product data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
  }, [productSlug, category]);

  if (loading) {
    return (
      <>
        <ContactBar />
        <div className="min-h-screen flex items-center justify-center bg-white pt-0">
          <Loader2 className="h-12 w-12 animate-spin text-jv-primary" />
        </div>
      </>
    );
  }

  // If this is a simple product, render SimpleProductPageClient
  if (isSimpleProduct) {
    return <SimpleProductPageClient />;
  }

  // If this is a variant page, render ProductVariantPageClient
  if (isVariantPage) {
    return <ProductVariantPageClient />;
  }

  // If redirecting to single variant page, show loading
  if (shouldRedirect) {
    return (
      <>
        <ContactBar />
        <div className="min-h-screen flex items-center justify-center bg-white pt-0">
          <Loader2 className="h-12 w-12 animate-spin text-jv-primary" />
        </div>
      </>
    );
  }

  if (!product) {
    return <NotFoundPage />;
  }

  const pageTitle = product.meta_title || `${product.name} kopen | Buitn Planten Dedemsvaart`;
  const pageDescription = product.meta_description || `${product.name} (${product.scientific_name}) kopen bij Buitn Planten. Topkwaliteit haagplanten uit Dedemsvaart. Bekijk beschikbare maten en bestel direct.`;
  const pageImage = variants[0]?.images[0] || 'https://buitnplanten.nl/assets/sections/hero.webp';
  const pageUrl = `https://buitnplanten.nl/${category}/${productSlug}`;
  const lowestPrice = variants.length > 0 ? Math.min(...variants.map(v => parseFloat(v.price_tier_1))) : null;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={`${product.name}, ${product.scientific_name}, haagplanten, ${product.category_name}, Buitn Planten, Dedemsvaart, kopen`} />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="product" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={pageImage} />
        {lowestPrice && <meta property="product:price:amount" content={lowestPrice.toFixed(2)} />}
        {lowestPrice && <meta property="product:price:currency" content="EUR" />}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />

        {/* Product Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": product.name,
            "description": pageDescription,
            "image": pageImage,
            "brand": {
              "@type": "Brand",
              "name": "Buitn Planten"
            },
            "category": product.category_name,
            ...(lowestPrice ? {
              "offers": {
                "@type": "Offer",
                "url": pageUrl,
                "priceCurrency": "EUR",
                "price": lowestPrice.toFixed(2),
                "availability": "https://schema.org/InStock",
                "seller": {
                  "@type": "Organization",
                  "name": "Buitn Planten"
                }
              }
            } : {})
          })}
        </script>

        {/* BreadcrumbList Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://buitnplanten.nl/" },
              { "@type": "ListItem", "position": 2, "name": product.category_name, "item": `https://buitnplanten.nl/${product.category_slug}` },
              ...(product.subcategory_name && product.subcategory_slug ? [{ "@type": "ListItem", "position": 3, "name": product.subcategory_name, "item": `https://buitnplanten.nl/${product.category_slug}/${product.subcategory_slug}` }] : []),
              { "@type": "ListItem", "position": product.subcategory_name ? 4 : 3, "name": product.name, "item": pageUrl }
            ]
          })}
        </script>

        {/* FAQPage Schema for product FAQs */}
        {faqs.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer.replace(/<[^>]*>/g, '')
                }
              }))
            })}
          </script>
        )}
      </Helmet>

      <ContactBar />
      <div className="min-h-screen bg-[#fcfaf6]">
        <Header />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
          {/* Shared breadcrumb */}
          <PageBreadcrumb items={[
            { label: 'Home', href: '/' },
            { label: product.category_name, href: `/${product.category_slug}` },
            ...(product.subcategory_name && product.subcategory_slug
              ? [{ label: product.subcategory_name, href: `/${product.category_slug}/${product.subcategory_slug}` } as Crumb]
              : []),
            { label: product.name },
          ]} />

          {/* Product header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-jv-dark leading-tight">
              Koop jouw {product.name} bij de haagplanten specialist in Twente
            </h1>
          </div>

          <section className="mb-16">
            {variants.length > 0 ? (
              <div className="space-y-4 lg:space-y-6">
                {variants.map((variant) => (
                  <ProductVariantCard
                    key={variant.id}
                    variant={variant}
                    productName={product.name}
                    productSlug={product.base_slug}
                    categorySlug={product.category_slug}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-600 text-lg">Er zijn momenteel geen varianten beschikbaar voor dit product.</p>
              </div>
            )}
          </section>

          {/* Plant info card */}
          {product.description && (
            <div className="mb-16 rounded-xl bg-white p-6 ring-1 ring-[#e7e1d6] lg:p-8">
              <div className="flex items-center gap-4 mb-5">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-[5px] bg-[#1e2b22]">
                  <img src="/assets/laurier-white.webp" alt="" className="h-7 w-auto" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-[#1e2b22]">{product.name}</h2>
                  {product.scientific_name && (
                    <p className="text-sm italic text-[#5a6552]">{product.scientific_name}</p>
                  )}
                </div>
              </div>
              <div
                className="text-base text-[#1a1a1a] leading-7 prose prose-base max-w-none"
                dangerouslySetInnerHTML={{ __html: descriptionHtml }}
              />
            </div>
          )}

          {faqs.length > 0 && (
            <div className="mb-16">
              <ProductFAQ faqs={faqs} />
            </div>
          )}

          <OfferteCTA />
        </main>

        <Footer />
      </div>
    </>
  );
}
